import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import * as React from "react";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/features/auth/auth-context";
import { createQueryClient } from "@/lib/query-client";
import { AppRoutes } from "@/routes";

/**
 * The provider stack, and the order is load-bearing.
 *
 *   BrowserRouter        — `AuthProvider` calls `useNavigate` to send an expired
 *                          session to the login screen, so the router must be
 *                          outside it.
 *   QueryClientProvider  — `AuthProvider` clears the cache on sign-out, so the
 *                          client has to exist before it.
 *   AuthProvider         — everything below can ask `can()` (§2.1).
 *
 * The QueryClient is created ONCE via `useState`, never inline: a fresh client
 * on every render would discard the whole cache whenever a parent re-rendered,
 * which on a workshop connection means refetching the register for nothing.
 */
export function App(): React.JSX.Element {
  const [queryClient] = React.useState(createQueryClient);

  return (
    /*
     * `ThemeProvider` was MISSING until now, and its absence was not harmless:
     * `src/components/ui/sonner.tsx` already calls `useTheme()`, which without a
     * provider always answered "system" — so a toast could render light on a
     * dark screen. `next-themes` was already a dependency for exactly this and
     * had simply never been mounted.
     *
     * `attribute="class"` because `index.css` defines the dark palette under a
     * `.dark` class and declares `@custom-variant dark (&:is(.dark *))`. Any
     * other attribute would leave every dark token unreachable.
     *
     * `disableTransitionOnChange` stops every colour on the page animating at
     * once when the theme flips, which reads as the app stalling.
     */
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
          {/*
            * `TooltipProvider` is required by the sidebar, which shows each nav
            * item's label as a tooltip once collapsed to icons. Without it those
            * items become unlabelled glyphs — which §11.1 rules out in as many
            * words ("never an icon alone — an unlabelled glyph is a guess").
            * One provider at the root; Radix shares the delay timer across every
            * tooltip in the tree, so hovering along the rail does not re-arm the
            * open delay on each item.
            */}
          <TooltipProvider>
            <AppRoutes />
          {/*
            * Sonner is shadcn's toast surface, mounted once here so `toast()` is
            * callable from anywhere without a provider per screen.
            */}
            {/*
              * ONE TOASTER, BOTTOM-RIGHT — and this is the only mount in the
              * app. A second one anywhere would give the same `toast()` call two
              * stacking contexts and two places to look.
              *
              * WHY BOTTOM-RIGHT RATHER THAN TOP-CENTRE (settled 2026-08-18; the
              * reasoning is recorded so it is not re-derived every few months):
              *
              *   POSITION IS NOT WHAT CARRIES URGENCY HERE. There are 52 toasts
              *   in this app — 24 success, 18 error, 5 warning, 4 info — and 33
              *   screens ALREADY render an inline destructive `Alert` for their
              *   load and save failures. The thing that must not be missed is
              *   therefore already inline and persistent, on the screen that
              *   caused it. What is left for the toast to carry is colour
              *   (`richColors`), not placement.
              *
              *   TOP-CENTRE PENALISES THE FREQUENT CASE FOR THE RARE ONE. On a
              *   phone it lands over the header — the back affordance and the
              *   title — and on a desktop it lands over the top of the document
              *   being edited. The common toast is "saved", which needs to be
              *   seen once and then gone; making it cover the work every time so
              *   that a rarer failure is unmissable trades constantly for
              *   occasionally, and the failure is not relying on the toast
              *   anyway.
              *
              * TOASTS ARE FOR THINGS THAT SUCCEEDED AND THEN GOT OUT OF THE WAY.
              * They are NOT where a failure goes: a toast disappears on a timer,
              * and §11.1 is explicit that the owner must be told what went wrong
              * in words he can still read a minute later. Errors belong in an
              * `Alert` on the screen that caused them.
              *
              * `richColors` gives success and error their own fills rather than
              * one neutral card, and `closeButton` means a toast that arrives
              * while he is reading something can be dismissed rather than waited
              * out.
              */}
            <Toaster richColors closeButton position="bottom-right" />
          </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
